import React, { useState } from 'react'
import { Heart, ExternalLink, Copy, TrendingUp, Users, Star, Filter, Grid, List } from 'lucide-react'

interface NFTCollectionProps {
  data: {
    collectionName: string
    description: string
    creator: string
    contractAddress: string
    totalSupply: number
    floorPrice: string
    volumeTraded: string
    owners: number
    verified: boolean
    socialLinks: {
      twitter: string
      discord: string
      website: string
    }
    nfts: Array<{
      id: string
      name: string
      image: string
      rarity: string
      attributes: Array<{
        trait_type: string
        value: string
        rarity_score: number
      }>
      lastSale?: {
        price: string
        currency: string
        date: string
      }
      currentPrice?: {
        price: string
        currency: string
      }
    }>
    stats: {
      totalVolume: string
      averagePrice: string
      highestSale: string
      listedCount: number
    }
  }
}

export default function NFTCollection({ data }: NFTCollectionProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState<'price' | 'rarity' | 'recent'>('price')
  const [filterRarity, setFilterRarity] = useState<string>('all')
  const [favorites, setFavorites] = useState<Set<string>>(new Set())

  const toggleFavorite = (nftId: string) => {
    const newFavorites = new Set(favorites)
    if (newFavorites.has(nftId)) {
      newFavorites.delete(nftId)
    } else {
      newFavorites.add(nftId)
    }
    setFavorites(newFavorites)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const getRarityColor = (rarity: string) => {
    switch (rarity.toLowerCase()) {
      case 'legendary': return 'bg-purple-500'
      case 'epic': return 'bg-blue-500'
      case 'rare': return 'bg-green-500'
      case 'uncommon': return 'bg-yellow-500'
      default: return 'bg-gray-500'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                NFT Gallery
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#collection" className="text-white/80 hover:text-white transition-colors">Collection</a>
              <a href="#activity" className="text-white/80 hover:text-white transition-colors">Activity</a>
              <a href="#analytics" className="text-white/80 hover:text-white transition-colors">Analytics</a>
              <a href="#about" className="text-white/80 hover:text-white transition-colors">About</a>
            </nav>
            <div className="flex items-center space-x-4">
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors">
                Connect Wallet
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Collection Hero */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Collection Image */}
            <div className="lg:w-1/3">
              <div className="aspect-square bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl border border-white/20 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Star className="w-12 h-12 text-purple-400" />
                  </div>
                  <p className="text-purple-300 text-lg">{data.collectionName}</p>
                </div>
              </div>
            </div>

            {/* Collection Info */}
            <div className="lg:w-2/3">
              <div className="flex items-center gap-4 mb-6">
                <h1 
                  className="text-4xl md:text-6xl font-bold"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  {data.collectionName}
                </h1>
                {data.verified && (
                  <div className="bg-gradient-to-r from-gold-400 to-gold-600 rounded-full p-2">
                    <Star className="w-6 h-6 text-white fill-current" />
                  </div>
                )}
              </div>
              
              <p className="text-xl text-purple-200 mb-8 leading-relaxed">
                {data.description}
              </p>

              {/* Collection Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-2xl font-bold text-purple-300">{data.floorPrice}</div>
                  <div className="text-sm text-purple-400">Floor Price</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-2xl font-bold text-pink-300">{data.volumeTraded}</div>
                  <div className="text-sm text-pink-400">Volume Traded</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-2xl font-bold text-blue-300">{data.totalSupply}</div>
                  <div className="text-sm text-blue-400">Total Supply</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-2xl font-bold text-green-300">{data.owners}</div>
                  <div className="text-sm text-green-400">Owners</div>
                </div>
              </div>

              {/* Contract Address */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/20 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-purple-400 mb-1">Contract Address</div>
                    <div className="font-mono text-sm text-white">
                      {data.contractAddress.slice(0, 6)}...{data.contractAddress.slice(-4)}
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(data.contractAddress)}
                    className="text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex gap-4">
                {Object.entries(data.socialLinks).map(([platform, url]) => (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Overview */}
      <section className="py-12 px-6 bg-black/30">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-bold mb-8 text-center"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Collection Statistics
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-purple-400" />
                <span className="text-purple-400 font-semibold">Total Volume</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.stats.totalVolume}</div>
            </div>
            
            <div className="bg-gradient-to-r from-pink-500/20 to-pink-600/20 backdrop-blur-sm rounded-xl p-6 border border-pink-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Star className="w-6 h-6 text-pink-400" />
                <span className="text-pink-400 font-semibold">Average Price</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.stats.averagePrice}</div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-400/30">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-blue-400" />
                <span className="text-blue-400 font-semibold">Highest Sale</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.stats.highestSale}</div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-green-400" />
                <span className="text-green-400 font-semibold">Listed</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.stats.listedCount}</div>
            </div>
          </div>
        </div>
      </section>

      {/* NFT Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Filters and Controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h2 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              NFT Collection
            </h2>
            
            <div className="flex gap-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-400"
              >
                <option value="price">Price: Low to High</option>
                <option value="rarity">Rarity: High to Low</option>
                <option value="recent">Recently Listed</option>
              </select>
              
              <select
                value={filterRarity}
                onChange={(e) => setFilterRarity(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-400"
              >
                <option value="all">All Rarities</option>
                <option value="legendary">Legendary</option>
                <option value="epic">Epic</option>
                <option value="rare">Rare</option>
                <option value="uncommon">Uncommon</option>
              </select>
              
              <div className="flex bg-white/10 rounded-lg border border-white/20">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-purple-500' : 'hover:bg-white/10'} transition-colors`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-purple-500' : 'hover:bg-white/10'} transition-colors`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* NFT Grid */}
          <div className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-1'
          }`}>
            {data.nfts.map((nft) => (
              <div
                key={nft.id}
                className={`bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 hover:border-purple-400/50 transition-all duration-300 group ${
                  viewMode === 'list' ? 'flex' : ''
                }`}
              >
                {/* NFT Image */}
                <div className={`bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center relative ${
                  viewMode === 'list' ? 'w-32 h-32 flex-shrink-0' : 'aspect-square'
                }`}>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Star className="w-8 h-8 text-purple-400" />
                    </div>
                    <p className="text-purple-300 text-sm">{nft.name}</p>
                  </div>
                  
                  {/* Rarity Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 rounded text-xs font-semibold text-white ${getRarityColor(nft.rarity)}`}>
                      {nft.rarity}
                    </span>
                  </div>
                  
                  {/* Favorite Button */}
                  <button
                    onClick={() => toggleFavorite(nft.id)}
                    className="absolute top-2 right-2 p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                  >
                    <Heart 
                      className={`w-4 h-4 ${
                        favorites.has(nft.id) 
                          ? 'text-red-500 fill-current' 
                          : 'text-white'
                      }`} 
                    />
                  </button>
                </div>
                
                {/* NFT Info */}
                <div className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-white text-lg">{nft.name}</h3>
                    <span className="text-purple-400 text-sm">#{nft.id}</span>
                  </div>
                  
                  {/* Price */}
                  <div className="mb-3">
                    {nft.currentPrice ? (
                      <div className="text-lg font-bold text-purple-300">
                        {nft.currentPrice.price} {nft.currentPrice.currency}
                      </div>
                    ) : (
                      <div className="text-gray-400">Not for sale</div>
                    )}
                  </div>
                  
                  {/* Last Sale */}
                  {nft.lastSale && (
                    <div className="text-sm text-gray-400 mb-3">
                      Last sale: {nft.lastSale.price} {nft.lastSale.currency}
                    </div>
                  )}
                  
                  {/* Top Attributes */}
                  <div className="space-y-2">
                    {nft.attributes.slice(0, 2).map((attr, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-400">{attr.trait_type}</span>
                        <span className="text-white font-medium">{attr.value}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-4 flex gap-2">
                    <button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 px-3 rounded-lg text-sm font-semibold transition-colors">
                      {nft.currentPrice ? 'Buy Now' : 'View'}
                    </button>
                    <button className="bg-white/10 hover:bg-white/20 text-white py-2 px-3 rounded-lg transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-white/10 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            {Object.entries(data.socialLinks).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-purple-400 transition-colors"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            ))}
          </div>
          <p className="text-gray-400">
            &copy; 2024 {data.collectionName}. Built on the blockchain.
          </p>
        </div>
      </footer>
    </div>
  )
}
